import { Link } from 'react-router-dom';
import { 
  ChevronDown,
  Building2,
  UserCircle,
  ShoppingBag,
  Users,
  Target,
  Heart,
  Award,
  MapPin,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

import { ASTERPOS_LOGO } from '@/config/brandAssets';

export default function AboutPage() {
  const values = [
    {
      icon: Heart,
      title: 'Restaurant First',
      description: 'Every feature we build starts with one question: Does this help restaurants succeed?'
    },
    {
      icon: Target,
      title: 'Simplicity',
      description: 'Technology should make things easier, not harder. We obsess over making complex tasks simple.'
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'We\'re not just a vendor. We\'re a partner in your success, growing together with every restaurant we serve.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Good enough isn\'t good enough. We strive for excellence in every line of code and every customer interaction.'
    }
  ];

  const milestones = [
    { year: '2022', event: 'Founded in San Francisco with a mission to modernize restaurant tech' },
    { year: '2023', event: 'Launched Cloud POS and Kitchen Display System' },
    { year: '2024', event: 'Expanded to 300+ restaurants across 15 states' },
    { year: '2025', event: 'Introduced integrated payments and advanced analytics' },
    { year: '2026', event: 'Serving 450+ restaurants with full-stack restaurant OS' }
  ];

  const team = [
    { 
      name: 'Kevin Dole', 
      role: 'CEO & Founder', 
      background: 'Former Engineer & CTO at AT&T',
      image: null // TODO: Add production image URL
    },
    { 
      name: 'Sarah Martinez', 
      role: 'CTO & Co-founder', 
      background: 'Built payments at Toast',
      image: null
    },
    { 
      name: 'Michael Park', 
      role: 'VP of Product', 
      background: '10 years in restaurant tech',
      image: null
    },
    { 
      name: 'Thu Hang Le', 
      role: 'VP of Customer Success', 
      background: 'Former SmartPay',
      image: null // TODO: Add production image URL
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-8 w-8" />
              <span className="text-xl font-bold text-white">Chain29</span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/product" className="text-slate-300 hover:text-white text-sm font-medium">
                Product
              </Link>
              <Link to="/pricing" className="text-slate-300 hover:text-white text-sm font-medium">
                Pricing
              </Link>
              <Link to="/support" className="text-slate-300 hover:text-white text-sm font-medium">
                Support
              </Link>
              <Link to="/about" className="text-white text-sm font-medium">
                About
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10">
                    Login
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/owner/login" className="flex items-center gap-2 cursor-pointer">
                      <Building2 className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Restaurant Owner</div>
                        <div className="text-xs text-muted-foreground">Manage your restaurant</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/staff/login" className="flex items-center gap-2 cursor-pointer">
                      <UserCircle className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Employee / Staff</div>
                        <div className="text-xs text-muted-foreground">Clock in & access POS</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/auth" className="flex items-center gap-2 cursor-pointer">
                      <ShoppingBag className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Customer</div>
                        <div className="text-xs text-muted-foreground">Order food online</div>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Link to="/owner/signup">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white">
                  Start Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Building the Future of<br />
            <span className="text-orange-400">Restaurant Technology</span>
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
            We're a team of restaurant tech veterans, engineers, and operators who believe 
            every restaurant deserves access to world-class technology.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-black/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
            <p className="text-xl text-slate-300 leading-relaxed">
              To empower restaurant owners with technology that was once only available to 
              big chains. We're democratizing restaurant tech, one feature at a time, so that 
              independent restaurants can compete and thrive in a digital world.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div 
                key={i}
                className="p-6 rounded-xl bg-white/5 border border-white/10 text-center"
              >
                <div className="p-3 rounded-lg bg-orange-500/20 w-fit mx-auto mb-4">
                  <value.icon className="w-6 h-6 text-orange-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{value.title}</h3>
                <p className="text-slate-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-black/20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our Journey</h2>
          <div className="max-w-2xl mx-auto">
            {milestones.map((milestone, i) => (
              <div key={i} className="flex gap-4 mb-6">
                <div className="w-16 flex-shrink-0">
                  <span className="text-orange-400 font-bold">{milestone.year}</span>
                </div>
                <div className="flex-1 pb-6 border-l-2 border-white/10 pl-4">
                  <p className="text-slate-300">{milestone.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Leadership Team</h2>
          <p className="text-slate-400 text-center mb-12">
            Built by people who understand restaurants
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {team.map((member, i) => (
              <div 
                key={i}
                className="p-6 rounded-xl bg-white/5 border border-white/10 text-center"
              >
                {member.image ? (
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-20 h-20 rounded-full mx-auto mb-4 object-cover object-top border-2 border-orange-500/30"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                )}
                <h3 className="text-lg font-semibold text-white">{member.name}</h3>
                <p className="text-orange-400 text-sm mb-2">{member.role}</p>
                <p className="text-slate-500 text-xs">{member.background}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-black/20">
        <div className="container mx-auto px-4 text-center">
          <MapPin className="w-8 h-8 text-orange-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Headquartered in San Francisco</h2>
          <p className="text-slate-400">
            With team members across the country, serving restaurants nationwide
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Join 450+ Restaurants?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Start your free trial today and see why restaurants love Chain29.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/owner/signup">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={ASTERPOS_LOGO} alt="Chain29" className="h-6 w-6" />
              <span className="text-white font-semibold">Chain29</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
              <Link to="/product" className="hover:text-white">Product</Link>
              <Link to="/pricing" className="hover:text-white">Pricing</Link>
              <Link to="/about" className="hover:text-white">About</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
            </div>
            <p className="text-sm text-slate-500">© 2026 Chain29. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
